// src/components/currencies/CurrenciesTable.tsx

import React from "react";
import ColumnFilter from "@/components/shared/ui/ColumnFilter";
import Input from "@/components/shared/ui/Input";
import { FiEdit2, FiTrash2, FiEye } from "react-icons/fi";

export type CurrencyTableFilters = {
  code?: string;
  name?: string;
  symbol?: string;
  country?: string;
};

export type CurrencyRow = {
  id: string | number;
  code: string;
  name: string;
  symbol?: string | null;
  minorUnit?: number | null;
  country?: string | null;
};

export default function CurrenciesTable({
  rows: _rows, // unused (kept for parity)
  pageRows,
  serialStart,
  filters,
  setFilters,
  onView,
  onEdit,
  onDelete,
}: {
  rows: CurrencyRow[];
  pageRows: CurrencyRow[];
  serialStart: number;
  filters: CurrencyTableFilters;
  setFilters: React.Dispatch<React.SetStateAction<CurrencyTableFilters>>;
  onView: (r: CurrencyRow) => void;
  onEdit: (r: CurrencyRow) => void;
  onDelete: (r: CurrencyRow) => void;
}) {
  return (
    // Square bottom: the pagination bar below completes this card.
    <div className="overflow-hidden rounded-t-2xl border border-gray-200 bg-white">
      {/* Desktop table */}
      <div className="hidden overflow-x-auto overflow-visible md:block">
        <table className="min-w-full text-left text-sm">
          <thead className="sticky top-0 z-10 bg-gray-50 text-gray-600">
            <tr className="border-b border-gray-200">
              <Th>S.No</Th>

              <Th>
                <div className="flex items-center gap-1.5">
                  <span>Code</span>
                  <ColumnFilter label="Code">
                    <Input
                      placeholder="Contains…"
                      value={filters.code || ""}
                      onChange={(e) =>
                        setFilters((f) => ({ ...f, code: e.target.value }))
                      }
                    />
                  </ColumnFilter>
                </div>
              </Th>

              <Th>
                <div className="flex items-center gap-1.5">
                  <span>Name</span>
                  <ColumnFilter label="Name">
                    <Input
                      placeholder="Contains…"
                      value={filters.name || ""}
                      onChange={(e) =>
                        setFilters((f) => ({ ...f, name: e.target.value }))
                      }
                    />
                  </ColumnFilter>
                </div>
              </Th>

              <Th>
                <div className="flex items-center gap-1.5">
                  <span>Symbol</span>
                  <ColumnFilter label="Symbol">
                    <Input
                      placeholder="Contains…"
                      value={filters.symbol || ""}
                      onChange={(e) =>
                        setFilters((f) => ({ ...f, symbol: e.target.value }))
                      }
                    />
                  </ColumnFilter>
                </div>
              </Th>

              <Th>
                <div className="flex items-center gap-1.5">
                  <span>Country</span>
                  <ColumnFilter label="Country">
                    <Input
                      placeholder="Contains…"
                      value={filters.country || ""}
                      onChange={(e) =>
                        setFilters((f) => ({ ...f, country: e.target.value }))
                      }
                    />
                  </ColumnFilter>
                </div>
              </Th>

              <Th className="text-right">Actions</Th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {pageRows.length === 0 ? (
              <tr>
                {/* 6 columns total */}
                <td
                  colSpan={6}
                  className="px-5 py-10 text-center text-sm text-gray-500"
                >
                  No currencies found.
                </td>
              </tr>
            ) : (
              pageRows.map((r, i) => (
                <tr key={r.id} className="hover:bg-gray-50/60">
                  <Td className="text-gray-700">{serialStart + i + 1}</Td>
                  <Td className="text-gray-900 font-medium">{r.code}</Td>
                  <Td className="text-gray-900">{r.name}</Td>
                  <Td className="text-gray-700">
                    {r.symbol ? (
                      r.symbol
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </Td>
                  <Td className="text-gray-700">
                    {r.country ? (
                      r.country
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </Td>
                  <Td>
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
                        onClick={() => onView(r)}
                        aria-label="View"
                        title="View"
                      >
                        <FiEye />
                      </button>

                      <button
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
                        onClick={() => onEdit(r)}
                        aria-label="Edit"
                        title="Edit"
                      >
                        <FiEdit2 />
                      </button>

                      <button
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-red-600 hover:bg-red-50"
                        onClick={() => onDelete(r)}
                        aria-label="Delete"
                        title="Delete"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile list */}
      <div className="divide-y divide-gray-200 md:hidden">
        {pageRows.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">
            No currencies found.
          </div>
        ) : (
          pageRows.map((r, i) => (
            <div key={r.id} className="p-4">
              <div className="flex items-center justify-between">
                <div className="pr-2">
                  <div className="text-sm font-medium text-gray-900">
                    {serialStart + i + 1}. {r.code} — {r.name}
                  </div>

                  <div className="mt-0.5 text-xs text-gray-600">
                    {r.symbol ? (
                      <span>Symbol: {r.symbol}</span>
                    ) : (
                      <span className="text-gray-400">Symbol: —</span>
                    )}
                    <span className="mx-2 text-gray-300">•</span>
                    {r.country ? (
                      <span>Country: {r.country}</span>
                    ) : (
                      <span className="text-gray-400">Country: —</span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
                    onClick={() => onView(r)}
                    aria-label="View"
                  >
                    <FiEye />
                  </button>
                  <button
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
                    onClick={() => onEdit(r)}
                    aria-label="Edit"
                  >
                    <FiEdit2 />
                  </button>
                  <button
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-red-600 hover:bg-red-50"
                    onClick={() => onDelete(r)}
                    aria-label="Delete"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function Th({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <th
      scope="col"
      className={`px-5 py-3.5 text-xs font-semibold uppercase tracking-wide ${className}`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <td className={`px-5 py-3.5 ${className}`}>{children}</td>;
}
